import dayjs from 'dayjs'

import { PersonaInsightRow } from '@/common/api/params/persona'

import {
  INSIGHT_STEPS,
  PersonaInsightState
} from '@/components/container/modules/persona/useInsight'

import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * ペルソナ状況示唆。
 *
 * **示唆を出すだけ。施策は提案しない**（販促はプロモーションカルテの領分）。
 * 目的は「何のためにペルソナに分けたか」を感じてもらうことなので、
 * 示唆はサイト全体との対比で書かれる。
 *
 * 状態は useInsight に切り出してある（ボタンと結果が離れた場所に出るため）。
 */

const QUESTION_LABEL: Record<string, string> = {
  bias: '偏り',
  movement: '動き',
  discovery: '気づき',
  operation: '運用'
}

const ACTION_LABEL: Record<string, string> = {
  activate: '有効化',
  deactivate: '無効化',
  priority: '優先度',
  measure: '測定',
  consolidate: '統合の検討'
}


/** 実行中の進捗。無言だと固まったように見えるので必ず出す */
export const InsightProgress = ({ state }: { state: PersonaInsightState }) => {
  if (!state.isMutating) return null
  return (
    <Text size={12} color={TextColor.SECONDARY}>
      {state.step}/{INSIGHT_STEPS.length}: {INSIGHT_STEPS[Math.max(0, state.step - 1)]}…（30秒ほどかかります）
    </Text>
  )
}

const InsightBody = ({ row }: { row: PersonaInsightRow }) => {
  const o = row.output
  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-wrap items-baseline gap-x-3 gap-y-1'>
        <Text size={13} weight='bold'>
          第{row.generation}世代
        </Text>
        <Text size={10} color={TextColor.TERTIARY}>
          {row.created_at ? dayjs(row.created_at).format('YYYY/MM/DD HH:mm') : ''}
          {row.operator_name ? ` ・ ${row.operator_name}` : ''}
          {` ・ 直近${row.days}日`}
          {row.model ? ` ・ ${row.model}` : ''}
        </Text>
      </div>

      {o.summary && <Text size={14}>{o.summary}</Text>}

      {o.findings.length > 0 && (
        <div className='flex flex-col gap-4'>
          {o.findings.map((f, i) => (
            <div key={`${f.question}-${i}`} className='flex flex-col gap-1'>
              <div className='flex flex-wrap items-baseline gap-2'>
                <Text size={10} color={TextColor.LINK} weight='bold'>
                  {QUESTION_LABEL[f.question ?? ''] ?? f.question}
                </Text>
                <Text size={13} weight='bold'>
                  {f.title}
                </Text>
              </div>
              <Text size={13} color={TextColor.SECONDARY}>
                {f.body}
              </Text>
              {f.personas.length > 0 && (
                <Text size={10} color={TextColor.TERTIARY}>
                  対象: {f.personas.join(', ')}
                </Text>
              )}
            </div>
          ))}
        </div>
      )}

      {o.operations.length > 0 && (
        <div className='flex flex-col gap-2'>
          <Text size={12} weight='bold'>
            運用の提案
          </Text>
          <Text size={10} color={TextColor.TERTIARY}>
            ペルソナそのものの扱いだけを提案します。クーポンやメールなどの施策は
            プロモーションカルテの担当です。
          </Text>
          {o.operations.map((op, i) => (
            <div key={`${op.action}-${i}`} className='flex flex-wrap items-baseline gap-2'>
              <Text size={10} color={TextColor.LINK} weight='bold' nowrap>
                {ACTION_LABEL[op.action ?? ''] ?? op.action}
              </Text>
              <Text size={12} weight='bold' nowrap>
                {op.persona_ids.join(', ')}
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                {op.reason}
              </Text>
            </div>
          ))}
        </div>
      )}

      {o.not_covered.length > 0 && (
        <div className='flex flex-col gap-1'>
          <Text size={12} weight='bold'>
            触れなかったこと
          </Text>
          <Text size={10} color={TextColor.TERTIARY}>
            データがまだ無い領域です。効果が無いという意味ではありません。
          </Text>
          {o.not_covered.map((x, i) => (
            <Text key={i} size={12} color={TextColor.SECONDARY}>
              ・{x}
            </Text>
          ))}
        </div>
      )}

      {row.warnings.length > 0 && (
        <div className='border-border-input flex flex-col gap-1 border-t pt-3'>
          <Text size={12} weight='bold'>
            この示唆についての注意
          </Text>
          {row.warnings.map((w, i) => (
            <Text key={i} size={12} color={TextColor.SECONDARY}>
              ・{w.message}
            </Text>
          ))}
        </div>
      )}
    </div>
  )
}

/** 結果と履歴。まだ1件も無いうちは何も描かない（空の枠を置かない） */
const InsightPanel = ({ state }: { state: PersonaInsightState }) => {
  if (state.isLoading) return null
  if (!state.error && state.items.length === 0) return null

  return (
    <Area>
      <Heading
        subText={`サイト全体との違いから、ペルソナに分けて見えたことを述べます。${state.keepGenerations}世代まで残ります。`}
      >
        AIペルソナ状況示唆
      </Heading>
      <AreaContent>
        <div className='flex flex-col gap-4'>
          {state.error && (
            <Text size={12} color={TextColor.CRITICAL}>
              {state.error}
            </Text>
          )}

          {state.items.length > 1 && (
            <div className='flex flex-wrap items-center gap-2'>
              <Text size={12} color={TextColor.SECONDARY}>
                履歴
              </Text>
              {state.items.map((x) => (
                <button
                  key={x.id}
                  type='button'
                  className={`text-size-12 rounded-lg border px-3 py-1 ${
                    x.id === state.current?.id
                      ? 'border-border-input bg-input font-bold'
                      : 'border-transparent'
                  }`}
                  onClick={() => state.setSelected(x.id)}
                >
                  第{x.generation}世代
                </button>
              ))}
            </div>
          )}

          {state.current && <InsightBody row={state.current} />}
        </div>
      </AreaContent>
    </Area>
  )
}

export default InsightPanel
